import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FaTimes } from 'react-icons/fa';

interface ModalShellProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'md' | 'lg';
  variant?: 'modal' | 'drawer';
  /** false = no close button, and Escape or a click outside does nothing (for steps the user must complete). */
  dismissible?: boolean;
}

export default function ModalShell({
  title,
  onClose,
  children,
  footer,
  size = 'md',
  variant = 'modal',
  dismissible = true,
}: ModalShellProps) {
  useEffect(() => {
    if (!dismissible) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, dismissible]);

  const isDrawer = variant === 'drawer';
  const panelClass = isDrawer
    ? 'ml-auto h-full w-full max-w-3xl'
    : `m-auto max-h-[90vh] w-full ${size === 'lg' ? 'max-w-2xl' : 'max-w-lg'} rounded-2xl`;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex bg-slate-900/50 p-0 backdrop-blur-[1px] sm:p-4"
      onClick={dismissible ? onClose : undefined}
      role="presentation"
    >
      <div
        className={`flex flex-col border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-800 ${panelClass}`}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-6 py-4 dark:border-slate-700">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-gray-100">{title}</h3>
          {dismissible && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-slate-700"
            >
              <FaTimes aria-hidden="true" />
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && (
          <div className="flex flex-wrap justify-end gap-3 border-t border-slate-200 px-6 py-4 dark:border-slate-700">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
